import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import styles from './progressbar.module.css';
import { useStyles } from '../../../../providers/styles/use-styles';

interface ProgressBarProps {
    secondsToFire: number;
    isPaused: boolean;
    onFired: () => void;
}

export interface ProgressBarHandle {
    pause: () => void;
    restart: () => void;
}

const ProgressBarComponent = forwardRef<ProgressBarHandle, ProgressBarProps>(
    ({ secondsToFire, isPaused, onFired }, ref) => {

        const { getStyles } = useStyles();
        const [progress, setProgress] = useState(0);
        const [isRunning, setIsRunning] = useState(!isPaused);
        const intervalRef = useRef<NodeJS.Timeout | null>(null);
        const startTimeRef = useRef<number>(0);
        const remainingTimeRef = useRef<number>(secondsToFire * 1000); // in milliseconds

        // Expose pause and restart methods to parent component
        useImperativeHandle(ref, () => ({
            pause: () => {
                setIsRunning(false);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                // Calculate remaining time when paused
                const elapsed = Date.now() - startTimeRef.current;
                remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
            },
            restart: () => {
                if (!isRunning) {
                    setIsRunning(true);
                    startTimeRef.current = Date.now();
                    startProgress();
                }
            }
        }));

        const startProgress = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }

            startTimeRef.current = Date.now();

            intervalRef.current = setInterval(() => {
                const elapsed = Date.now() - startTimeRef.current;
                const newProgress = Math.min(100, (elapsed / (secondsToFire * 1000)) * 100);

                setProgress(newProgress);

                if (newProgress >= 100) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                    }
                    onFired();
                }
            }, 16); // Update every ~16ms (~60fps)
        };

        useEffect(() => {
            if (!isPaused) {
                startProgress();
            }

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            };
        }, [secondsToFire, onFired]);

        return (
            <div className={styles.progressBarContainer}>
                <div
                    className={styles.progressbar}
                    style={{ width: `${progress}%`, height: `100%`, backgroundColor: getStyles(['bg_card_primary_base'])['backgroundColor'] }}
                ></div>
            </div>
        );
    }
);

const ProgressBar = React.memo(ProgressBarComponent, (prevProps, nextProps) => {
    return prevProps.secondsToFire === nextProps.secondsToFire &&
        prevProps.isPaused === nextProps.isPaused;
});

export default ProgressBar;
